import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import {
  customContactFields,
  salesgearContactFields,
  salesgearFieldMapping,
  zohoContactFieldMapping,
  zohoLeadFieldMapping,
  getCustomFieldsCRM
} from "static/constant";
import SettingsData, { settingsStoreData } from "Stores/settingsData";
import FilterDropdown from "../../Triggers/AddEditTrigger/FilterDropdown";
import AddItem from "../../Triggers/AddEditTrigger/AddItem";
import { ReactComponent as OutboundIcon } from "Assets/svg/Outbound.svg";
import { ReactComponent as InboundIcon } from "Assets/svg/Inbound.svg";
import { ReactComponent as BothDirectionsIcon } from "Assets/svg/BothDirections.svg";
import AutoCompleteDropdown from "Components/common/AutoCompleteDropdown";
import userDetail from "Stores/userDetail";

const TableHeader = props => {
  const { type = "CONTACTS", crmName = "" } = props;
  const tableHeader = [
    {
      name: "Salesgear Contact Field",
      key: "salesgearContactFields",
      style: { width: "33.3%" }
    },
    {
      name: "Sync (Direction)",
      key: "syncFirection",
      style: { width: "33.3%" },
      className: settingsStoreData?.fromContacts ? "highlightContactExport" : ""
    },
    {
      name:
        type === "LEADS" ? `${crmName} Lead Field` : `${crmName} Contact Field`,
      key: "crmContactFields",
      style: { width: "33.3%" }
    },
    { style: { width: "20px" } }
  ];

  return (
    <thead>
      <tr>
        {tableHeader?.map(item => {
          const {
            name = "",
            key = "",
            style = {},
            className = ""
          } = item || {};
          return (
            <th className={`textDark ${className}`} key={key} style={style}>
              {name}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

const SyncDirection = observer(props => {
  const {
    activeDropdownIndex = "",
    setActiveDropdownIndex = () => {},
    currentSyncDirection = "INBOUND",
    defaultSyncDirection = "INBOUND",
    index = 0,
    className = "",
    crmFieldRestrictAccess = true,
    config = {},
    setConfig = () => {}
  } = props;

  const syncDirectionList = [
    {
      value: "OUTBOUND",
      name: "Outbound",
      Icon: OutboundIcon,
      hasIcon: true
    },
    {
      value: "INBOUND",
      name: "Inbound",
      Icon: InboundIcon,
      hasIcon: true
    },
    {
      value: "BOTH",
      name: "Both Directions",
      Icon: BothDirectionsIcon,
      hasIcon: true
    }
  ];

  const mappedSyncDirection = {
    OUTBOUND: "Outbound",
    INBOUND: "Inbound",
    BOTH: "Both Directions",
    OUTBOUND_ICON: <OutboundIcon />,
    INBOUND_ICON: <InboundIcon />,
    BOTH_ICON: <BothDirectionsIcon />
  };

  const SyncDirectionIcon =
    mappedSyncDirection?.[`${currentSyncDirection}_ICON`] ||
    mappedSyncDirection?.[`${defaultSyncDirection}_ICON`];

  const syncDirectionAction = item => {
    const indexValue = Number(activeDropdownIndex.split("-")?.[1]);
    const fields = [...config?.fields];
    fields[indexValue - 1] = {
      ...config?.fields?.[indexValue - 1],
      syncDirection: item?.value
    };
    setConfig({
      ...config,
      fields
    });
    setActiveDropdownIndex("");
  };

  const getDefaultValue = () => {
    return (
      <>
        <span className="selectedSyncDirectionIcon">{SyncDirectionIcon}</span>
        <span className="selectedSyncDirection">
          {mappedSyncDirection?.[currentSyncDirection] ||
            mappedSyncDirection?.[defaultSyncDirection]}
        </span>
      </>
    );
  };

  return (
    <FilterDropdown
      label="Select"
      isCustomValue={true}
      className={className}
      hasDefaultChildren={true}
      hasCustomFieldValue={true}
      options={syncDirectionList}
      index={`keysync-${index + 1}`}
      customCbk={syncDirectionAction}
      accessToEditField={crmFieldRestrictAccess}
      setActiveDropdownIndex={setActiveDropdownIndex}
      showDropdown={activeDropdownIndex === `keysync-${index + 1}`}
      defaultValue={getDefaultValue()}
    />
  );
});

const TableBody = observer(props => {
  const {
    mappingConfig = [],
    type = "CONTACTS",
    config = [],
    setConfig = () => {},
    fields: currentFields = {},
    crmName = ""
  } = props || {};

  const { customFieldsCount = 0 } = userDetail?.userFeatureFlag || {};

  const [activeDropdownIndex, setActiveDropdownIndex] = useState("");
  const [crmOptionList, setCrmOptionList] = useState([]);
  const [deleteIconIndex, setDeleteIconIndex] = useState("");
  const [updatedMappingConfig, setUpdatedMappingConfig] =
    useState(mappingConfig);
  const [disabledSgFields, setDisabledSgFields] = useState([]);
  const [disabledCrmFields, setDisabledCrmFields] = useState([]);
  const [crmMappedConfig, setCrmMappedConfig] = useState({});

  const getCustomFields = (item, index, type) => {
    const indexValue = Number(index.split("-")[1]);
    const fields = [...config?.fields];
    fields[indexValue - 1] = {
      ...config?.fields?.[indexValue - 1],
      ...(type === "salesgearField"
        ? { sgField: item?.value }
        : { crmField: item?.value })
    };
    setUpdatedMappingConfig(fields);
    type === "salesgearField"
      ? setDisabledSgFields([...disabledSgFields, item?.value])
      : setDisabledCrmFields([...disabledCrmFields, item?.value]);
    setConfig({
      ...config,
      fields
    });
    setActiveDropdownIndex("");
  };

  const customCrmFieldCbk = (item, index) => {
    getCustomFields(item, index, "crmField");
  };

  const customSgFieldCbk = (item, index) => {
    getCustomFields(item, index, "salesgearField");
  };

  const handleDeleteMapping = index => {
    const fields = [...config?.fields];
    const { crmField = "", sgField = "" } = fields?.[index];
    fields.splice(index, 1);
    setConfig({
      ...config,
      fields
    });
    setUpdatedMappingConfig(fields);
    setDisabledSgFields(disabledSgFields?.filter(item => item !== sgField));
    setDisabledCrmFields(disabledCrmFields?.filter(item => item !== crmField));
  };

  const addCbk = () => {
    const fields = [...config?.fields];
    const updatedFieldMapping = [
      ...fields,
      {
        crmField: "",
        sgField: "",
        syncDirection: "INBOUND",
        syncEnabled: false
      }
    ];
    setUpdatedMappingConfig(updatedFieldMapping);
    setConfig({
      ...config,
      fields: updatedFieldMapping
    });
  };

  const getAlreadyFilledValue = () => {
    setDisabledSgFields(config?.fields?.map(item => item?.sgField));
    setDisabledCrmFields(config?.fields?.map(item => item?.crmField));
  };

  const getFieldMappingConfig = () => {
    let tempCustomFields = getCustomFieldsCRM(customFieldsCount);
    let tempConfig = {};
    tempCustomFields?.length > 0 &&
      tempCustomFields.map(item => {
        tempConfig = {
          ...tempConfig,
          [item.value]:
            settingsStoreData?.customFields?.[item?.value] || item?.name
        };
      });
    return { ...salesgearFieldMapping, ...tempConfig };
  };

  const constructCrmFields = () => {
    const updatedCrmOptions = currentFields?.fieldList?.map(item => {
      return {
        value: item?.crmField,
        name:
          crmName === "Zoho"
            ? item?.crmField?.replace(/_/g, " ")
            : item?.crmFieldLabel?.replace(/_/g, " "),
        disabled: disabledCrmFields?.includes(item?.crmField)
      };
    });
    setCrmOptionList(updatedCrmOptions);
    if (crmName === "Hubspot") {
      const mappedCrmField =
        updatedCrmOptions?.length > 0 &&
        updatedCrmOptions?.reduce(function (accumulator, item) {
          accumulator[item?.value] = item?.name;
          return accumulator;
        }, {});
      setCrmMappedConfig(mappedCrmField);
    }
  };

  const constructSalesgearContactFields = () => {
    return (
      (salesgearContactFields?.length > 0 &&
        salesgearContactFields.map(item => {
          item.disabled = disabledSgFields?.includes(item?.value);
          return item;
        })) ||
      []
    );
  };

  const constructCustomFieldsWithValue = () => {
    let tempCustomFields = getCustomFieldsCRM(customFieldsCount) || [];
    return (
      (tempCustomFields?.length > 0 &&
        tempCustomFields.map(item => {
          item.name =
            settingsStoreData?.customFields?.[item?.value] || item?.name;
          item.hasCustomFieldValue = settingsStoreData?.customFields?.[
            item?.value
          ]
            ? true
            : false;
          item.disabled = disabledSgFields?.includes(item?.value);
          return item;
        })) ||
      []
    );
  };

  useEffect(() => {
    config?.fields && getAlreadyFilledValue();
  }, [config?.fields]);

  useEffect(() => {
    constructCrmFields();
  }, [currentFields, disabledCrmFields]);

  return (
    <tbody>
      {updatedMappingConfig?.length > 0 &&
        updatedMappingConfig.map((item, index) => {
          const currentConfig = config?.fields?.[index];
          const {
            sgField: defaultSgField = "",
            crmField: defaultCrmField = "",
            syncDirection: defaultSyncDirection = ""
          } = item || {};
          const {
            sgField: currentSgField = "",
            crmField: currentCrmField = "",
            syncDirection: currentSyncDirection = ""
          } = currentConfig || {};

          const mappedFields = getFieldMappingConfig();
          const sgOptionList = [
            { value: "", name: "None" },
            ...constructSalesgearContactFields(),
            ...constructCustomFieldsWithValue()
          ];
          const defaultCRMField =
            type === "LEADS"
              ? zohoLeadFieldMapping?.[defaultCrmField]
              : zohoContactFieldMapping?.[defaultCrmField];

          // Restrict stageDisplayName to OUTBOUND only (can't sync from CRM to SG)
          const isStageField = currentSgField === "stageDisplayName" || defaultSgField === "stageDisplayName";

          const crmFieldRestrictAccess = ![
            "last name",
            "company",
            "lastName"
          ]?.includes(defaultCRMField?.toLowerCase()) && !isStageField;

          return (
            <tr
              key={`${defaultSgField}-${Date.now()}-${index + 1}`}
              onMouseEnter={() => setDeleteIconIndex(index)}
              onMouseLeave={() => setDeleteIconIndex("")}
            >
              <td className="sgField" style={{ width: "33.3%" }}>
                <AutoCompleteDropdown
                  optionList={sgOptionList}
                  defaultValue={
                    currentSgField === ""
                      ? currentSgField
                      : mappedFields?.[currentSgField] ||
                        mappedFields?.[defaultSgField]
                  }
                  cbk={customSgFieldCbk}
                  index={`keySgField-${index + 1}`}
                  loader={!sgOptionList}
                  crm={true}
                />
              </td>
              <td
                className={`syncDirectionField ${
                  settingsStoreData?.fromContacts
                    ? "highlightContactExport"
                    : ""
                }`}
                style={{ width: "33.3%" }}
                onClick={() => settingsStoreData.setFromContacts(false)}
              >
                <SyncDirection
                  type={type}
                  index={index}
                  defaultSyncDirection={defaultSyncDirection}
                  currentSyncDirection={isStageField ? "OUTBOUND" : currentSyncDirection}
                  activeDropdownIndex={activeDropdownIndex}
                  setActiveDropdownIndex={setActiveDropdownIndex}
                  className={`${!crmFieldRestrictAccess ? "disabled" : ""}`}
                  crmFieldRestrictAccess={crmFieldRestrictAccess}
                  config={config}
                  setConfig={setConfig}
                />
              </td>
              <td className="crmField" style={{ width: "33.3%" }}>
                <AutoCompleteDropdown
                  optionList={crmOptionList}
                  defaultValue={
                    crmName === "Zoho"
                      ? currentCrmField?.replace(/_/g, " ")
                      : crmMappedConfig?.[currentCrmField]
                  }
                  cbk={customCrmFieldCbk}
                  index={`keyCrmField-${index + 1}`}
                  loader={!crmOptionList}
                  crm={true}
                />
              </td>
              <td style={{ width: "20px" }}>
                <i
                  className="material-icons deleteIcon"
                  style={{
                    display: deleteIconIndex === index ? "flex" : "none"
                  }}
                  onClick={() => handleDeleteMapping(index)}
                >
                  delete
                </i>
              </td>
            </tr>
          );
        })}
      <tr>
        <td>
          <AddItem addCbk={addCbk} name="Add" />
        </td>
      </tr>
    </tbody>
  );
});

const FieldMapping = observer(props => {
  const { mappingConfig = [], type = "CONTACTS", ...remainingProps } = props;

  return (
    <div className="fieldMappingWrapper">
      <h6 className="headingText">Field mapping</h6>
      <div className="fieldMappingTable tableView">
        <table
          className={`${
            settingsStoreData?.fromContacts ? "highlightContactExportTable" : ""
          }`}
        >
          <TableHeader type={type} {...remainingProps} />
          <TableBody
            mappingConfig={mappingConfig}
            type={type}
            {...remainingProps}
          />
        </table>
      </div>
    </div>
  );
});

export { FieldMapping };
export default FieldMapping;
